<template>
  <a
    class="city-around-card"
    :href="props.url"
    @click="handleCityAroundItemClick"
  >
    <ui-image
      class="city-around-card__image"
      :photo="props.image"
      :alt="props.cityName"
      fit="cover"
    />

    <div class="city-around-card__content">
      <div class="city-around-card__name">{{ cityName }}</div>
      <div class="city-around-card__experience-count">{{ experienceCountDescription }}</div>
    </div>
  </a>
</template>

<script setup lang="ts">
  import { choosePlural } from '@experience/shared';
  import { excursionCount } from '@/shared/consts';
  import { UiImage } from '@experience/core-image-ui';
  import type { ContextGeoSubject } from '@/shared/types/geography.ts';
  import { trackEvent } from '@/entities/sogu';

  interface Props {
    url: string;
    image?: {
      thumbnail_s?: string;
      thumbnail_m?: string;
      thumbnail_l?: string;
      thumbnail?: string;
    };
    cityName: string;
    experienceCount: number;
    defaultSoguFields?: ContextGeoSubject;
  }

  const props: Props = defineProps<Props>();

  const experienceCountDescription = computed(
    () => props.experienceCount + ' ' + choosePlural(props.experienceCount, excursionCount.items_pluralized),
  );

  const handleCityAroundItemClick = () => {
    trackEvent('listing:around.click', getCityAroundListViewEventParams());
  };

  const getCityAroundListViewEventParams = () => ({ ...props.defaultSoguFields?.soguStamp, city_name: props.cityName });
</script>

<style scoped lang="scss">
  .city-around-card {
    display: grid;
    gap: 8px;
    justify-content: center;
    overflow: hidden;
    box-sizing: border-box;
    max-height: min-content;
    padding: 4px 4px 8px;
    border-radius: var(--alias-radius-12);
    background-color: var(--alias-color-background-subtle);
    text-decoration: none;
  }

  .city-around-card__image {
    display: block;
    object-fit: cover;
    overflow: hidden;
    width: 100%;
    border-radius: var(--alias-radius-8);
    aspect-ratio: 4 / 3;

    :deep(picture),
    :deep(img) {
      display: block;
      object-fit: cover;
      width: 100%;
      height: 100%;
    }
  }

  .city-around-card__content {
    font-weight: var(--global-typography-weight-regular);
    font-size: var(--global-typography-size-300);
    text-align: center;
  }

  .city-around-card__name {
    display: -webkit-box;
    overflow: hidden;
    color: var(--alias-color-text-default);
    line-height: var(--global-typography-line-height-300);
    word-break: break-word;
    hyphens: auto;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
  }

  .city-around-card__experience-count {
    color: var(--alias-color-text-subtle);
    font-size: var(--global-typography-size-200);
    line-height: var(--global-typography-line-height-200);
  }

  @media only screen and (min-width: 1024px) {
    .city-around-card {
      position: relative;
      display: block;
      max-width: none;
      padding: 0;
      background-color: var(--alias-color-background-subtle);
      text-decoration: none;

      &:before {
        content: '';
        position: absolute;
        inset: 0;
        z-index: 1;
        border-radius: var(--alias-radius-8);
        background: linear-gradient(180deg, transparent 29.96%, var(--global-color-transparent-black-a700) 99.81%);
      }

      &:hover {
        .city-around-card__image {
          transform: scale(1.1);
        }
      }
    }

    .city-around-card__image {
      position: relative;
      width: 100%;
      transition: transform 0.3s ease;
    }

    .city-around-card__content {
      position: absolute;
      bottom: 0;
      z-index: 2;
      padding: 0 12px 12px;
      font-size: var(--global-typography-size-400);
      line-height: var(--global-typography-line-height-400);
      text-align: start;
    }

    .city-around-card__name,
    .city-around-card__experience-count {
      color: var(--alias-color-text-inverse);
    }

    .city-around-card__name {
      font-weight: var(--global-typography-weight-semi);
    }
  }
</style>
